import React from "react";
import type {
  BaselineControlResponse,
  ControlOrigination,
  ImplementationStatus,
  SspComponentResponse,
} from "../../../../../api/frameworksApi";
import {
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
  Modal,
  ModalFooterButtons,
} from "../../../../components/Modal";
import { IMPLEMENTATION_STATUSES } from "../../constants";
import {
  buildParameterRows,
  findBaselineControlForId,
} from "../../utils/parameters";

import type { EditControlImplFormShape } from "../../types";
export type { EditControlImplFormShape };

export interface EditControlImplementationDialogProps {
  open: boolean;
  onClose: () => void;
  handleUpdateControlImpl: () => void;
  savingControlImpl: boolean;
  editControlImplForm: EditControlImplFormShape;
  setEditControlImplForm: React.Dispatch<
    React.SetStateAction<EditControlImplFormShape>
  >;
  sspComponents: SspComponentResponse[];
  baselineControls: BaselineControlResponse[];
}

export function EditControlImplementationDialog({
  open,
  onClose,
  handleUpdateControlImpl,
  savingControlImpl,
  editControlImplForm,
  setEditControlImplForm,
  sspComponents,
  baselineControls,
}: EditControlImplementationDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Control Implementation"
      description={`Editing implemented requirement for ${editControlImplForm.controlId}`}
      size="md"
      footer={
        <ModalFooterButtons
          onCancel={onClose}
          onSubmit={handleUpdateControlImpl}
          submitLabel={savingControlImpl ? "Saving" : "Save"}
          submitDisabled={
            !editControlImplForm.controlId ||
            !editControlImplForm.implementationStatus ||
            savingControlImpl
          }
        />
      }
    >
      {/* Components (multi-select with per-component description) */}
      <FormField label="Components">
        {sspComponents.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No components available.
          </p>
        ) : (
          <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] bg-[#f5f7fb] dark:bg-white/[0.04] max-h-72 overflow-y-auto">
            {sspComponents.map((c) => {
              const checked = editControlImplForm.byComponents.some(
                (bc) => bc.componentUuid === c.componentUuid,
              );
              const bcEntry = editControlImplForm.byComponents.find(
                (bc) => bc.componentUuid === c.componentUuid,
              );
              return (
                <div
                  key={c.componentUuid}
                  className="border-b border-[#e2e8f0]/50 dark:border-white/[0.04] last:border-0"
                >
                  {/* Checkbox row */}
                  <label className="flex items-center gap-2.5 px-3 py-2 hover:bg-black/[0.03] dark:hover:bg-white/[0.03] cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        setEditControlImplForm((f) => ({
                          ...f,
                          byComponents: checked
                            ? f.byComponents.filter(
                              (bc) => bc.componentUuid !== c.componentUuid,
                            )
                            : [
                              ...f.byComponents,
                              {
                                componentUuid: c.componentUuid,
                                description: "",
                                implementationStatus: "",
                                responsibleRoles: "",
                              },
                            ],
                        }))
                      }
                      className="size-3.5 rounded border-border accent-indigo-600 cursor-pointer flex-shrink-0"
                    />
                    <span className="text-sm text-foreground">
                      {c.title}{" "}
                      <span className="text-xs text-muted-foreground">
                        ({c.componentType})
                      </span>
                    </span>
                  </label>

                  {/* Per-component description — shown when checked */}
                  {checked && (
                    <div className="px-3 pb-2.5">
                      <p className="text-[10px] text-muted-foreground mb-1 font-medium tracking-wide">
                        How does <span className="text-indigo-400">{c.title}</span> implement this control?
                      </p>
                      <textarea
                        rows={2}
                        value={bcEntry?.description ?? ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          setEditControlImplForm((f) => ({
                            ...f,
                            byComponents: f.byComponents.map((bc) =>
                              bc.componentUuid === c.componentUuid
                                ? { ...bc, description: val }
                                : bc,
                            ),
                          }));
                        }}
                        placeholder={`Describe how ${c.title} satisfies this control...`}
                        className="w-full rounded-md border border-[#e2e8f0] dark:border-white/[0.07] bg-background px-2.5 py-1.5 text-xs text-foreground outline-none transition-colors resize-none placeholder:text-muted-foreground hover:border-indigo-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </FormField>

      {/* Control ID + Implementation Status (same line) */}
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Control ID">
          <FormInput
            value={editControlImplForm.controlId}
            onChange={() => { }}
            placeholder="Control ID"
            disabled
          />
        </FormField>
        <FormField label="Implementation Status" required>
          <FormSelect
            options={IMPLEMENTATION_STATUSES}
            value={editControlImplForm.implementationStatus}
            onChange={(v) =>
              setEditControlImplForm((f) => ({
                ...f,
                implementationStatus: v as ImplementationStatus,
              }))
            }
            placeholder="Select status"
          />
        </FormField>
      </div>

      {/* Responsible Roles */}
      <FormField label="Responsible Role IDs" hint="Comma-separated role IDs (e.g. isso, system-owner). Stored as OSCAL responsible-roles on this implemented-requirement.">
        <FormInput
          placeholder="e.g. isso, system-owner, system-engineer"
          value={editControlImplForm.responsibleRoleIds}
          onChange={(v) =>
            setEditControlImplForm((f) => ({ ...f, responsibleRoleIds: v }))
          }
        />
      </FormField>

      {/* Overall implementation description */}
      <FormField
        label="Overall Implementation Description"
        hint="OSCAL: implemented-requirement.description — summarises how the system as a whole satisfies this control"
      >
        <FormTextarea
          value={editControlImplForm.description}
          onChange={(v) =>
            setEditControlImplForm((f) => ({ ...f, description: v }))
          }
          placeholder="Describe how this control is satisfied by the system overall..."
          rows={3}
        />
      </FormField>

      {/* Parameters from catalog / profile */}
      {(() => {
        const bc = findBaselineControlForId(
          baselineControls,
          editControlImplForm.controlId,
        );
        const parameterRows = buildParameterRows(
          bc,
          editControlImplForm.setParameters,
        );

        if (parameterRows.length > 0) {
          return (
            <div>
              <p className="text-sm font-medium text-foreground mb-2">
                Parameters
              </p>
              <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] overflow-hidden">
                {parameterRows.map((param, i) => (
                  <div
                    key={param.paramId}
                    className={`px-3 py-2.5 ${i !== parameterRows.length - 1 ? "border-b border-[#e2e8f0] dark:border-white/[0.07]" : ""}`}
                  >
                    {(() => {
                      const sspEntry = editControlImplForm.setParameters.find(
                        (p) => p.paramId === param.paramId,
                      );
                      const sspVal = sspEntry?.values?.trim();
                      const resolvedVal = sspVal
                        ? sspVal
                        : param.componentValues.length
                          ? param.componentValues.join(", ")
                          : param.profileValues.length
                            ? param.profileValues.join(", ")
                            : param.catalogValues.join(", ");
                      const resolvedSource = sspVal
                        ? "SSP override"
                        : param.componentValues.length
                          ? "Component"
                          : param.profileValues.length
                            ? "Profile"
                            : "Catalog default";
                      const sourceColor: Record<string, string> = {
                        "SSP override": "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400",
                        Component: "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
                        Profile: "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300",
                        "Catalog default": "bg-muted text-muted-foreground",
                      };
                      return (
                        <>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-mono text-indigo-400">
                              {param.paramId}
                            </span>
                            {param.label && (
                              <span className="text-xs text-muted-foreground">
                                — {param.label}
                              </span>
                            )}
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${sourceColor[resolvedSource]}`}>
                              {resolvedSource}
                            </span>
                            {resolvedVal && (
                              <span className="text-xs text-foreground font-medium truncate max-w-[160px]" title={resolvedVal}>
                                {resolvedVal}
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                            <div className="rounded-md bg-muted/40 p-2">
                              <span className="block text-muted-foreground mb-0.5">
                                Catalog default
                              </span>
                              <span className="text-foreground">
                                {param.catalogValues.length
                                  ? param.catalogValues.join(", ")
                                  : "-"}
                              </span>
                            </div>
                            <div className="rounded-md bg-muted/40 p-2">
                              <span className="block text-muted-foreground mb-0.5">
                                Profile / Component
                              </span>
                              <span className="text-foreground">
                                {param.profileValues.length
                                  ? param.profileValues.join(", ")
                                  : param.componentValues.length
                                    ? param.componentValues.join(", ")
                                    : "-"}
                              </span>
                            </div>
                            <div className="rounded-md bg-muted/40 p-2">
                              <span className="block text-muted-foreground mb-0.5 font-medium text-indigo-600 dark:text-indigo-400">
                                SSP override
                              </span>
                              <input
                                type="text"
                                value={sspEntry?.values ?? ""}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setEditControlImplForm((form) => {
                                    const existingIdx = form.setParameters.findIndex(
                                      (p) => p.paramId === param.paramId,
                                    );
                                    const nextParams = [...form.setParameters];
                                    if (existingIdx >= 0) {
                                      nextParams[existingIdx] = { ...nextParams[existingIdx], values: value };
                                    } else {
                                      nextParams.push({ paramId: param.paramId, values: value });
                                    }
                                    return { ...form, setParameters: nextParams };
                                  });
                                }}
                                placeholder="Override value (comma-separated)"
                                onClick={(e) => e.stopPropagation()}
                                className="mt-1 w-full rounded-md border border-indigo-200 dark:border-indigo-500/30 bg-background px-2 py-1.5 text-xs text-foreground outline-none transition-colors placeholder:text-muted-foreground hover:border-indigo-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                              />
                            </div>
                          </div>
                        </>
                      );
                    })()}
                    {param.constraints.length > 0 && (
                      <div className="text-xs mt-2">
                        <span className="text-muted-foreground">
                          Constraints:{" "}
                        </span>
                        <span className="text-foreground">
                          {param.constraints.join(", ")}
                        </span>
                      </div>
                    )}
                    {param.guidelines.length > 0 && (
                      <div className="text-xs mt-1">
                        <span className="text-muted-foreground">
                          Guidelines:{" "}
                        </span>
                        <span className="text-foreground">
                          {param.guidelines.join("; ")}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        }

        const topParams = (bc?.parameters ?? []).filter((p) =>
          p.paramId?.trim(),
        );
        const detailParams = (bc?.controlDetails?.params ?? []).filter(
          (p) =>
            p.paramId?.trim() &&
            (p.label?.trim() ||
              (p.values ?? []).length > 0 ||
              (p.guidelines ?? []).length > 0),
        );

        if (topParams.length > 0) {
          return (
            <div>
              <p className="text-sm font-medium text-foreground mb-2">
                Parameters
              </p>
              <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] overflow-hidden">
                {topParams.map((p, i) => (
                  <div
                    key={p.paramId}
                    className={`px-3 py-2.5 ${i !== topParams.length - 1 ? "border-b border-[#e2e8f0] dark:border-white/[0.07]" : ""}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-indigo-400">
                        {p.paramId}
                      </span>
                      {p.label && (
                        <span className="text-xs text-muted-foreground">
                          — {p.label}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-muted-foreground">
                          Catalog values:{" "}
                        </span>
                        <span className="text-foreground">
                          {p.catalogValues?.length
                            ? p.catalogValues.join(", ")
                            : "—"}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">
                          Profile values:{" "}
                        </span>
                        <span className="text-foreground">
                          {p.profileValues?.length
                            ? p.profileValues.join(", ")
                            : "—"}
                        </span>
                      </div>
                      {p.constraints?.length > 0 && (
                        <div className="col-span-2">
                          <span className="text-muted-foreground">
                            Constraints:{" "}
                          </span>
                          <span className="text-foreground">
                            {p.constraints.join(", ")}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        }

        if (detailParams.length > 0) {
          return (
            <div>
              <p className="text-sm font-medium text-foreground mb-2">
                Parameters
              </p>
              <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] overflow-hidden">
                {detailParams.map((p, i) => (
                  <div
                    key={p.paramId}
                    className={`px-3 py-2.5 ${i !== detailParams.length - 1 ? "border-b border-[#e2e8f0] dark:border-white/[0.07]" : ""}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-indigo-400">
                        {p.paramId}
                      </span>
                      {p.label && (
                        <span className="text-xs text-muted-foreground">
                          — {p.label}
                        </span>
                      )}
                    </div>
                    {p.values && p.values.length > 0 && (
                      <div className="text-xs">
                        <span className="text-muted-foreground">
                          Values:{" "}
                        </span>
                        <span className="text-foreground">
                          {p.values.join(", ")}
                        </span>
                      </div>
                    )}
                    {p.guidelines && p.guidelines.length > 0 && (
                      <div className="text-xs mt-0.5">
                        <span className="text-muted-foreground">
                          Guidelines:{" "}
                        </span>
                        <span className="text-foreground">
                          {p.guidelines.map((g) => g.prose).join("; ")}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        }

        return null;
      })()}
    </Modal>
  );
}
